import { proxy } from "valtio";

const STORAGE_KEY = "vizi-developer-settings";
const DEV_PASSWORD = "1234";

export type SpeechToTextProvider = "local" | "cloud";

/**
 * Developer settings store
 */
interface DeveloperStore {
  /** Whether developer mode is unlocked for this session */
  developerUnlocked: boolean;
  /** Whether to show InstantDB devtools overlay */
  instantDbDevtools: boolean;
  /** Whether to show canned descriptions dropdown in description page */
  showCannedDescriptions: boolean;
  /** Whether to accelerate mock processing (200ms per stage instead of 2s) */
  accelerateMockProcessing: boolean;
  /** Whether to show preprocessing analytics panel in video selection */
  showPreprocessingAnalytics: boolean;
  /** Whether to use mock backend (simulated) instead of real backend */
  useMockBackend: boolean;
  /** Speech-to-text provider: 'local' (Whisper in-browser) or 'cloud' (Cloudflare Workers) */
  speechToTextProvider: SpeechToTextProvider;
  /** Whether user has completed the onboarding animation */
  hasCompletedOnboarding: boolean;
  /** Whether to run web-based AV analytics during upload (can be slow on fast networks) */
  runWebAnalytics: boolean;
}

/**
 * Read persisted developer settings from localStorage
 */
function loadPersistedSettings(): Partial<DeveloperStore> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // Ignore parse errors
  }
  return {};
}

/**
 * Persist developer settings to localStorage
 */
function persistSettings(settings: Partial<DeveloperStore>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // Ignore storage errors
  }
}

const persisted = loadPersistedSettings();

export const developerStore = proxy<DeveloperStore>({
  developerUnlocked: persisted.developerUnlocked ?? false,
  instantDbDevtools: persisted.instantDbDevtools ?? false,
  showCannedDescriptions: persisted.showCannedDescriptions ?? false,
  accelerateMockProcessing: persisted.accelerateMockProcessing ?? false,
  showPreprocessingAnalytics: persisted.showPreprocessingAnalytics ?? false,
  useMockBackend: persisted.useMockBackend ?? true, // Default to mock mode
  speechToTextProvider: persisted.speechToTextProvider ?? "local", // Default to local Whisper
  hasCompletedOnboarding: persisted.hasCompletedOnboarding ?? false, // Default to not completed
  runWebAnalytics: persisted.runWebAnalytics ?? false, // Default to OFF - can slow down fast uploads
});

/**
 * Attempt to unlock developer mode with password
 * @returns true if password was correct
 */
export function unlockDeveloperMode(password: string): boolean {
  if (password === DEV_PASSWORD) {
    developerStore.developerUnlocked = true;
    persistAllSettings();
    return true;
  }
  return false;
}

/**
 * Lock developer mode
 */
export function lockDeveloperMode() {
  developerStore.developerUnlocked = false;
  persistAllSettings();
}

/**
 * Toggle InstantDB devtools setting
 * Note: Requires page refresh to take effect
 */
export function setInstantDbDevtools(enabled: boolean) {
  developerStore.instantDbDevtools = enabled;
  persistAllSettings();
}

/**
 * Toggle canned descriptions dropdown visibility
 */
export function setShowCannedDescriptions(enabled: boolean) {
  developerStore.showCannedDescriptions = enabled;
  persistAllSettings();
}

/**
 * Toggle accelerated mock processing
 */
export function setAccelerateMockProcessing(enabled: boolean) {
  developerStore.accelerateMockProcessing = enabled;
  persistAllSettings();
}

/**
 * Toggle preprocessing analytics panel visibility
 */
export function setShowPreprocessingAnalytics(enabled: boolean) {
  developerStore.showPreprocessingAnalytics = enabled;
  persistAllSettings();
}

/**
 * Toggle mock backend mode
 * When enabled, uses simulated processing (no real backend required)
 * When disabled, attempts to use real backend API
 */
export function setUseMockBackend(enabled: boolean) {
  developerStore.useMockBackend = enabled;
  persistAllSettings();
}

/**
 * Set speech-to-text provider
 * 'local' = In-browser Whisper (offline capable, ~290MB model)
 * 'cloud' = Cloudflare Workers AI (faster, requires network)
 */
export function setSpeechToTextProvider(provider: SpeechToTextProvider) {
  developerStore.speechToTextProvider = provider;
  persistAllSettings();
}

/**
 * Toggle web-based AV analytics during upload
 * When enabled, runs preprocessing (audio detection, transcription, vision analysis) in browser
 * When disabled, uploads complete faster (analytics run server-side)
 */
export function setRunWebAnalytics(enabled: boolean) {
  developerStore.runWebAnalytics = enabled;
  persistAllSettings();
}

/**
 * Mark onboarding as completed
 * Called after the onboarding animation finishes
 */
export function completeOnboarding() {
  developerStore.hasCompletedOnboarding = true;
  persistAllSettings();
}

/**
 * Reset onboarding state (for developer testing)
 * This will cause the onboarding animation to play again on next app load
 */
export function resetOnboarding() {
  developerStore.hasCompletedOnboarding = false;
  persistAllSettings();
}

/**
 * Helper to persist all settings at once
 */
function persistAllSettings() {
  persistSettings({
    developerUnlocked: developerStore.developerUnlocked,
    instantDbDevtools: developerStore.instantDbDevtools,
    showCannedDescriptions: developerStore.showCannedDescriptions,
    accelerateMockProcessing: developerStore.accelerateMockProcessing,
    showPreprocessingAnalytics: developerStore.showPreprocessingAnalytics,
    useMockBackend: developerStore.useMockBackend,
    speechToTextProvider: developerStore.speechToTextProvider,
    hasCompletedOnboarding: developerStore.hasCompletedOnboarding,
    runWebAnalytics: developerStore.runWebAnalytics,
  });
}

/**
 * Check if InstantDB devtools should be enabled
 * Called at module load time, before Valtio is initialized
 */
export function shouldEnableInstantDbDevtools(): boolean {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const settings = JSON.parse(stored);
      return settings.instantDbDevtools === true;
    }
  } catch {
    // Ignore errors
  }
  return false; // Default to disabled
}

/**
 * Check if onboarding has been completed
 * Called at module load time, before Valtio is initialized
 */
export function hasCompletedOnboardingCheck(): boolean {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const settings = JSON.parse(stored);
      return settings.hasCompletedOnboarding === true;
    }
  } catch {
    // Ignore errors
  }
  return false; // Default to not completed
}
